import { defineComponent as _defineComponent } from 'vue';
import { vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createVNode as _createVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys } from "vue";
const _hoisted_1 = ["disabled", "required", "min", "max", "tabindex"];
const _hoisted_2 = {
    key: 0,
    class: "number-input__controls"
};
const _hoisted_3 = ["disabled"];
const _hoisted_4 = ["disabled"];
import { ref, watch, onMounted } from 'vue';
import Icon from "./Icon.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'NumberInput',
    props: {
        value: {},
        min: {},
        max: {},
        increment: {},
        decimals: {},
        required: { type: Boolean },
        disabled: { type: Boolean },
        tabIndex: {},
        hideControls: { type: Boolean },
        clampingMode: {}
    },
    emits: [
        'change',
        'validityChange',
        'focus',
        'blur',
    ],
    setup(__props, { expose: __expose, emit: __emit }) {
        /**
         * Testcases:
         *
         * When making changes on this class, the following things have to be tested in the MIGA project:
         *
         *  * Selecting a quantity and adding the item to shopping cart must work.
         *  * Overriding prices for normal articles must work. It must be possible to increase or to decrease the prices.
         *  * Override prices for articles with customer-individual pricing: It must be possible to increase the price.
         *    Lowering the price must not be possible (Save-button is disabled).
         *  * Sales: You can only add as many items to the shopping cart as there are is inventory.
         */
        const emit = __emit;
        const props = __props;
        const numberInput = ref('');
        const invalid = ref(false);
        const numberInputRef = ref(null);
        __expose({
            numberInputRef
        });
        watch(() => numberInput.value, onNumberInputChange);
        watch(() => props.max, onMaxChange);
        watch(() => props.min, onMinChange);
        watch(() => props.value, reset);
        onMounted(() => {
            watch(() => invalid.value, onInvalidChange);
        });
        reset();
        function setNumber(_number, source) {
            const current = parseFloat(numberInput.value) || 0;
            let number = parseFloat(_number.toFixed(props.decimals ?? 0));
            if (isNaN(number)) {
                invalid.value = true;
                return;
            }
            if (props.increment !== undefined && props.increment !== 0) {
                if (source === "button") {
                    if (number > current) {
                        // Increasing
                        number = Math.ceil(number / props.increment) * props.increment;
                    }
                    else {
                        // Decreasing
                        number = Math.floor(number / props.increment) * props.increment;
                    }
                }
                else if (number % props.increment !== 0) {
                    invalid.value = true;
                }
            }
            if (props.min !== undefined && number < props.min) {
                if (props.clampingMode === 'immediately' || source === "button") {
                    number = props.min;
                }
                else {
                    invalid.value = true;
                }
            }
            if (props.max !== undefined && number > props.max) {
                if (props.clampingMode === 'immediately' || source === "button") {
                    number = props.max;
                }
                else {
                    invalid.value = true;
                }
            }
            // Update form input field value if the number was adjusted (e.g. because of min/max restrictions)
            if (current !== _number
                || number !== _number && numberInput.value.replace(/\.0*$/, '') !== '' + number) {
                numberInput.value = '' + number;
            }
            // don't emit invalid changes
            if (!invalid.value) {
                emit('change', number);
            }
        }
        function increase(amount) {
            !props.disabled && setNumber((parseFloat(numberInput.value) || 0) + (amount || 1), "button");
        }
        function decrease(amount) {
            !props.disabled && setNumber((parseFloat(numberInput.value) || 0) - (amount || 1), "button");
        }
        function onClickUp() {
            increase();
            emit('focus');
        }
        function onClickDown() {
            decrease();
            emit('focus');
        }
        function onNumberInputChange(inputValue) {
            invalid.value = false;
            let numberInputSanitized = inputValue
                .replace(/[^\d.]/, '')
                .replace(/\.+/, '.');
            if (numberInputSanitized !== inputValue) {
                numberInput.value = numberInputSanitized;
                invalid.value = true;
                return;
            }
            const number = parseFloat(inputValue);
            setNumber(number, "input");
        }
        function onMaxChange() {
            if (props.max !== undefined) {
                const value = parseFloat(numberInput.value);
                if (!isNaN(value)) {
                    numberInput.value = '' + Math.min(props.max, value);
                }
            }
        }
        function onInvalidChange() {
            emit('validityChange', !invalid.value);
        }
        function onMinChange() {
            if (props.min !== undefined) {
                const value = parseFloat(numberInput.value);
                if (!isNaN(value)) {
                    numberInput.value = '' + Math.max(props.min, value);
                }
            }
        }
        function reset() {
            if (typeof props.value === 'number') {
                // if the user types '23.', we emit '23'. When we receive it back,
                //   we don't want to remove the '.' the user just typed.
                if (numberInput.value.replace(/\.0*$/, '') !== '' + props.value) {
                    numberInput.value = '' + props.value;
                }
            }
            else {
                numberInput.value = '';
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["number-input", { 'number-input--invalid': invalid.value && !__props.disabled }]),
                onKeyup: [
                    _cache[3] || (_cache[3] = _withKeys(_withModifiers(($event) => (increase()), ["exact", "prevent"]), ["up"])),
                    _cache[4] || (_cache[4] = _withKeys(_withModifiers(($event) => (decrease()), ["exact", "prevent"]), ["down"])),
                    _cache[5] || (_cache[5] = _withKeys(_withModifiers(($event) => (increase(10)), ["shift", "exact", "prevent"]), ["up"])),
                    _cache[6] || (_cache[6] = _withKeys(_withModifiers(($event) => (decrease(10)), ["shift", "exact", "prevent"]), ["down"]))
                ]
            }, [
                _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((numberInput).value = $event)),
                    disabled: __props.disabled,
                    required: __props.required,
                    min: __props.min,
                    max: __props.max,
                    ref_key: "numberInputRef",
                    ref: numberInputRef,
                    tabindex: __props.tabIndex ?? 0,
                    onFocus: _cache[1] || (_cache[1] = ($event) => (emit('focus'))),
                    onBlur: _cache[2] || (_cache[2] = ($event) => (emit('blur'))),
                    type: "text"
                }, null, 40, _hoisted_1), [
                    [_vModelText, numberInput.value]
                ]),
                _cache[8] || (_cache[8] = _createTextVNode()),
                (!__props.hideControls)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("a", {
                            onClick: _withModifiers(onClickUp, ["prevent"]),
                            disabled: __props.disabled,
                            class: _normalizeClass(["number-input__control", { 'number-input__plus--disabled': __props.disabled || (__props.max !== undefined && parseFloat(numberInput.value) >= __props.max) }]),
                            tabindex: "-1",
                            href: ""
                        }, [
                            _createVNode(Icon, { name: "angle-up" })
                        ], 10, _hoisted_3),
                        _cache[7] || (_cache[7] = _createTextVNode()),
                        _createElementVNode("a", {
                            onClick: _withModifiers(onClickDown, ["prevent"]),
                            disabled: __props.disabled,
                            class: _normalizeClass(["number-input__control", { 'number-input__minus--disabled': __props.disabled || (__props.min !== undefined && parseFloat(numberInput.value) <= __props.min) }]),
                            tabindex: "-1",
                            href: ""
                        }, [
                            _createVNode(Icon, { name: "angle-down" })
                        ], 10, _hoisted_4)
                    ]))
                    : _createCommentVNode("", true)
            ], 34));
        };
    }
});
